import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import Modal from "../../common/Modal";
import { votersAPI } from "../../../services/api";
import toast from "react-hot-toast";
import { validateElectionTask } from "../../../validation/voters/electionTask";

/**
 * Combined Election Task Modal - handles both Add and Edit modes
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.task - Task object for edit mode (null/undefined for add mode)
 * @param {Function} props.onSave - Callback when task is saved (receives task data)
 */
export default function ElectionTaskModal({
    isOpen,
    onClose,
    task,
    onSave,
}) {
    const isEditMode = !!task;
    const queryClient = useQueryClient();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [status, setStatus] = useState("Pending");
    const [priority, setPriority] = useState("Medium");
    const [error, setError] = useState(null);

    // Helper function to parse ISO date string to YYYY-MM-DD format for date input
    const parseDateForInput = (dateString) => {
        if (!dateString) return "";
        // Handle both ISO format (2019-08-24T14:15:22Z) and YYYY-MM-DD format
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Helper function to convert backend status to display format
    const parseStatusForDisplay = (statusValue) => {
        if (!statusValue) return "";
        const lower = statusValue.toLowerCase();
        if (lower === "in-progress" || lower === "in progress") return "In Progress";
        if (lower === "completed") return "Completed";
        if (lower === "pending") return "Pending";
        // If it's already in display format, return as is
        return statusValue;
    };

    // Initialize form with task data in edit mode
    useEffect(() => {
        if (isOpen) {
            setError(null); // Clear error when modal opens
        }

        if (task) {
            setTitle(task.title || "");
            setDescription(task.description || "");
            setDueDate(parseDateForInput(task.due_date || task.dueDate || ""));
            setStatus(parseStatusForDisplay(task.status || ""));
            setPriority(capitalize(task.priority || ""));
        } else {
            // Reset form for add mode
            setTitle("");
            setDescription("");
            setDueDate("");
            setStatus("");
            setPriority("");
        }
    }, [task, isOpen]);

    const capitalize = (value) => {
        if (!value) return "";
        const lower = value.toLowerCase().replace(/[_-]/g, " ");
        if (lower.includes("progress")) return "In Progress";
        if (lower === "completed") return "Completed";
        if (lower === "pending") return "Pending";
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    // Create mutation for creating tasks
    const createMutation = useMutation(
        (taskData) => votersAPI.createElectionTask(taskData),
        {
            onSuccess: (response) => {
                const savedTask = response.data?.data || response.data;
                onSave(savedTask);

                // Invalidate and refetch election tasks query
                queryClient.invalidateQueries(["election-tasks"]);

                // Reset form
                setTitle("");
                setDescription("");
                setDueDate("");
                setStatus("");
                setPriority("");
                setError(null);
                toast.success("Task created successfully");
                onClose();
            },
            onError: (err) => {
                console.error("Error creating task:", err);
                const message = err.response?.data?.message || err.message || "Failed to create task. Please try again.";
                setError(message);
                toast.error(message);
            },
        }
    );

    // Update mutation for editing tasks
    const updateMutation = useMutation(
        ({ id, taskData }) => votersAPI.editElectionTask(id, taskData),
        {
            onSuccess: (response) => {
                const savedTask = response.data?.data || response.data;
                onSave(savedTask);

                // Invalidate and refetch election tasks query
                queryClient.invalidateQueries(["election-tasks"]);

                setError(null);
                toast.success("Task updated successfully");
                onClose();
            },
            onError: (err) => {
                console.error("Error updating task:", err);
                const message = err.response?.data?.message || err.message || "Failed to update task. Please try again.";
                setError(message);
                toast.error(message);
            },
        }
    );

    // Use the appropriate mutation based on mode
    const mutation = isEditMode ? updateMutation : createMutation;
    const isLoading = mutation.isLoading;

    // Helper function to convert status to backend format (lowercase)
    const formatStatus = (statusValue) => {
        if (!statusValue) return "";
        const lower = statusValue.toLowerCase();
        if (lower === "in progress") return "in-progress";
        return lower;
    };

    // Helper function to convert priority to backend format (lowercase)
    const formatPriority = (priorityValue) => {
        if (!priorityValue) return "";
        return priorityValue.toLowerCase();
    };

    // Helper function to format date to ISO 8601 with time
    const formatDueDate = (dateString) => {
        if (!dateString) return null;
        // Convert date input (YYYY-MM-DD) to ISO 8601 format with time (YYYY-MM-DDTHH:MM:SSZ)
        // Default to end of day in UTC
        const date = new Date(dateString + "T23:59:59Z");
        return date.toISOString();
    };

    const handleSubmit = async () => {
        // Client-side validation via schema
        const raw = {
            title: title,
            description: description,
            dueDate: dueDate, // YYYY-MM-DD
            status: status,
            priority: priority,
        };
        const result = await validateElectionTask(raw);
        if (!result.valid) {
            const firstError = Object.values(result.errors || {})[0] || "Please fix the highlighted errors";
            setError(firstError);
            toast.error(firstError);
            return;
        }

        setError(null);

        const taskData = {
            title: result.values.title.trim(),
            description: (result.values.description || "").trim(),
            due_date: formatDueDate(result.values.dueDate),
            status: formatStatus(result.values.status),
            priority: formatPriority(result.values.priority),
        };

        if (isEditMode) {
            mutation.mutate({ id: task.id, taskData });
        } else {
            mutation.mutate(taskData);
        }
    };

    const footer = (
        <div className="flex justify-end gap-3">
            <button
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-2 rounded-lg border hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Cancel
            </button>
            <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading ? "Saving..." : isEditMode ? "Update" : "Add"}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEditMode ? "Edit Task" : "Add New Task"}
            size="3xl"
            footer={footer}
            useHeadlessUI={false}
            alignment="top"
        >
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                </div>
            )}

            <hr className="border-gray-300 w-[calc(102%+2rem)] -mx-6 my-6" />

            {/* Task Name & Due Date */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-2">Task Name</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                            if (error) setError(null);
                        }}
                        placeholder="e.g., Verify registered voters list"
                        className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div className="flex-1">
                    <label className="block text-sm font-medium mb-2">Due Date</label>
                    <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => {
                            setDueDate(e.target.value);
                            if (error) setError(null);
                        }}
                        className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
            </div>

            {/* Description */}
            <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                        if (error) setError(null);
                    }}
                    placeholder="e.g., Check voter list accuracy for each precinct"
                    className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows={3}
                />
            </div>

            {/* Status */}
            <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Status</label>
                <div className="flex gap-2">
                    {["Pending", "In Progress", "Completed"].map((s) => (
                        <label
                            key={s}
                            className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm transition
                ${status === s
                                    ? "border-blue-600 bg-blue-50 text-blue-700"
                                    : "border-gray-300 bg-white text-gray-700"
                                }
              `}
                        >
                            <input
                                type="radio"
                                name="status"
                                value={s}
                                checked={status === s}
                                onChange={() => {
                                    setStatus(s);
                                    if (error) setError(null);
                                }}
                                className="accent-blue-600"
                            />
                            {s}
                        </label>
                    ))}
                </div>
            </div>

            {/* Priority */}
            <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Priority</label>
                <div className="flex gap-2">
                    {["High", "Medium", "Low"].map((p) => (
                        <label
                            key={p}
                            className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm transition
                ${priority === p
                                    ? "border-blue-600 bg-blue-50 text-blue-700"
                                    : "border-gray-300 bg-white text-gray-700"
                                }
              `}
                        >
                            <input
                                type="radio"
                                name="priority"
                                value={p}
                                checked={priority === p}
                                onChange={() => {
                                    setPriority(p);
                                    if (error) setError(null);
                                }}
                                className="accent-blue-600"
                            />
                            {p}
                        </label>
                    ))}
                </div>
            </div>

            <hr className="border-gray-300 w-[calc(102%+2rem)] -mx-6 my-6" />
        </Modal>
    );
}

